import type {
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  GlobalPositionStrategy,
  Overlay,
  OverlayRef,
} from '@angular/cdk/overlay';
import type { ComponentRef, ElementRef, EventEmitter, Provider, Type } from '@angular/core';
import { InjectionToken } from '@angular/core';
import type { SafeResourceUrl } from '@angular/platform-browser';

import type { Observable } from 'rxjs';

import type { Ability } from '@core/abilities-types';
import type { IconName } from '@core/user-interface-legacy';
import type {
  RendererCallbackFn,
  WalkthroughStepId,
  WalkthroughStepOrigin,
} from '@core/walkthrough-types';

import type { WalkthroughDialogComponent } from './ui/walkthrough-dialog/walkthrough-dialog.component';
import type { WalkthroughPostComponent } from './ui/walkthrough-post/walkthrough-post.component';
import type { WalkthroughTooltipComponent } from './ui/walkthrough-tooltip/walkthrough-tooltip.component';

export type WalkthroughComponentT =
  | WalkthroughTooltipComponent
  | WalkthroughPostComponent
  | WalkthroughDialogComponent;

type RenderOptions<K> = K extends WalkthroughTooltipComponent
  ? {
      strategy: (
        overlay: Overlay,
        origin: ElementRef,
        positions?: ConnectedPosition[],
        offset?: Offset,
      ) => FlexibleConnectedPositionStrategy;
      useWalkthroughBackdropClass: true;
      positions: ConnectedPosition[];
      offset?: Offset;
    }
  : {
      strategy: (overlay: Overlay) => GlobalPositionStrategy;
      useWalkthroughBackdropClass?: boolean;
      positions?: never;
      offset?: Offset;
    };

export type WalkthroughInputs =
  | WalkthroughCommonInputs
  | WalkthroughTooltipInputs
  | WalkthroughPostInputs
  | WalkthroughDialogInputs;

type ComponentData<Component> = {
  definition: Type<Component>;
  inputs: WalkthroughInputs;
};

export type Step = {
  id: WalkthroughStepId;
  position: number;
  walkThroughType?: WalkthroughType;
  component: ComponentData<WalkthroughComponentT>;
  renderOptions: RenderOptions<WalkthroughComponentT>;
  finishHandler?: () => void;
  abilities?: Ability[];
  features?: WalkthroughFeatureFilter[];
  isHidden?: boolean;
  disableOnboardingEndpoint?: boolean;
  afterDisposeCallback?: () => void;
  beforeRenderCallback?: () => void;
};

export enum WalkthroughState {
  'Finished' = 'finished',
  'Active' = 'active',
  'Canceled' = 'canceled',
}

export type WalkthroughCommonInputs = {
  heading: string;
  description: string;
  ctaText: string;
  ctaHandler?: () => void;
  ctaIcon?: IconName;
  closeText?: string;
  closeIcon?: IconName;
  stepsTotal?: number;
  currentStep?: number;
  ofText?: string;
};

export type WalkthroughTooltipInputs = {
  arrowConfig: [ArrowPosition, ArrowType];
  showStepCounter?: boolean;
  hasNewBadge?: boolean;
  hasTipBadge?: boolean;
};

export type WalkthroughPostInputs = {
  helpArticleKey?: string;
};

export type WalkthroughDialogInputs = {
  videoUrl?: SafeResourceUrl;
  imageUrl?: string | Observable<string>;
  showCloseButton?: boolean;
  learnMoreText?: string;
  dialogHelpArticle?: string;
  displayCtaOnly?: boolean;
  showSkipButton?: boolean;
};

export interface WalkthroughComponentProperties {
  next: EventEmitter<void>;
  cancel: EventEmitter<void>;
  finish: EventEmitter<void>;
}

export interface Renderer {
  render: (
    component: Type<WalkthroughComponentT>,
    renderStrategy: Step['renderOptions'],
    originDirective: WalkthroughStepOrigin,
  ) => void;
  dispose: (callback?: RendererCallbackFn, args?: void | MouseEvent) => void;
}

export type RenderRef = {
  componentRef: ComponentRef<WalkthroughComponentT>;
  overlayRef: OverlayRef;
  originDirective: WalkthroughStepOrigin;
};

export enum WalkthroughType {
  Pipelines = 'pipelines',
  Jobs = 'jobs',
  PublishJobs = 'publish_jobs',
  PipelineOverview = 'pipeline_overview',
  DisqualifyReasons = 'disqualify_reasons',
  EvaluateCandidate = 'evaluate_candidate',
  ScheduleInterview = 'schedule_interview',
  CollaborativeHiring = 'collaborative_hiring',
  ProceedDisqualify = 'proceed_disqualify',
  EmailTemplates = 'email_templates',
  BrowseJobs = 'browse_jobs',
  Marketplace = 'marketplace',
  MarketplaceToolbox = 'marketplace_toolbox',
  JobDescription = 'job_description',
  NavigationTour = 'navigation_tour',
}

export const WalkthroughMode = new InjectionToken<WalkthroughType>('WalkthroughMode');

// TODO consider union type or pairs for arrowConfig property

export type ArrowPosition = 'middle' | 'bottom' | 'top' | 'left' | 'right';
export type ArrowType = 'left' | 'right' | 'top' | 'bottom';

export type Offset = {
  offsetX: number;
  offsetY: number;
};

export function WalkthroughProvider(walkthroughType: WalkthroughType): Provider {
  return {
    provide: WalkthroughMode,
    useValue: walkthroughType,
  };
}

export interface WalkthroughServiceI {
  start: () => void;
}

export enum TooltipType {
  FirstTimeDrawerClose,
  GuideDismissed,
}

export enum WalkthroughFeatureFilter {
  CareersSite = 'careersSite',
}
